/**
 * Operations · Quality Control.
 */
import { useMemo, useState } from "react";
import { ShieldCheck, CheckCircle2, PauseCircle, XCircle } from "lucide-react";
import { Card, SectionHeader, TextInput, SeverityBadge, Button } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { DocumentActions } from "../../components/DocumentActions";
import { useDeepLink } from "../../lib/integration";
import { QC_CHECKS, QcStatus, QcCheck } from "../../mocks/operationsData";
import { api } from "../../services/api";
import { upsertLocal } from "../../services/store";

const STATUS_META: Record<QcStatus, { icon: any; label: string; tint: string; fg: string }> = {
  pass: { icon: CheckCircle2, label: "Pass", tint: "var(--ph-success-tint)", fg: "var(--ph-success)" },
  hold: { icon: PauseCircle,  label: "Hold", tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
  fail: { icon: XCircle,      label: "Fail", tint: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"  },
};

export default function Quality() {
  const { q: initialQ } = useDeepLink();
  const [tab, setTab] = useState<QcStatus | "all">("all");
  const [q, setQ]     = useState(initialQ);
  const [selected, setSelected] = useState<QcCheck | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: QC_CHECKS.length };
    QC_CHECKS.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => QC_CHECKS.filter((r) =>
      (tab === "all" || r.status === tab) &&
      (!q || r.batchCode.toLowerCase().includes(q.toLowerCase()) || r.result.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  const passRate = counts.pass ? Math.round((counts.pass / QC_CHECKS.length) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="op-quality">
      <Card testId="op-qc-summary">
        <SectionHeader eyebrow="This week" title="Quality summary" action={<ShieldCheck size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Pass rate</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>{passRate}%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Passes</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{counts.pass || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Holds</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-warning)" }}>{counts.hold || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Fails</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-danger)" }}>{counts.fail || 0}</div>
          </div>
        </div>
      </Card>

      <Card testId="op-qc-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search batch or result…" testId="op-qc-search" /></div>
        </div>
        <div className="mt-4">
          <TabBar<QcStatus | "all">
            testId="op-qc-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",  label: "All",  count: counts.all  },
              { key: "pass", label: "Pass", count: counts.pass || 0 },
              { key: "hold", label: "Hold", count: counts.hold || 0 },
              { key: "fail", label: "Fail", count: counts.fail || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="op-qc-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Checks" title={`${filtered.length} record${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-2" data-testid="op-qc-list">
          {filtered.map((r) => {
            const m = STATUS_META[r.status];
            const Icon = m.icon;
            return (
              <ListRow
                key={r.id}
                testId={`op-qc-${r.id}`}
                onClick={() => setSelected(r)}
                icon={
                  <span className="inline-flex items-center justify-center" style={{
                    width: 28, height: 28, borderRadius: "999px",
                    background: m.tint, color: m.fg,
                  }}>
                    <Icon size={13} />
                  </span>
                }
                label={
                  <span className="inline-flex items-center gap-2">
                    <span className="font-mono text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>{r.batchCode}</span>
                    <span className="capitalize">{r.checkType.replace("-", " ")}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.tint, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {m.label}
                    </span>
                  </span>
                }
                sublabel={<><span style={{ color: "var(--ph-ink-default)" }}>{r.result}</span> · {r.performedBy} · {r.performedRelative}</>}
                trailing={
                  <>
                    {typeof r.score === "number" && (
                      <span className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>
                        {r.score}
                      </span>
                    )}
                    {r.severity && <SeverityBadge severity={r.severity} />}
                  </>
                }
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Quality 360" testId="op-qc-drawer" width={540}>
        {selected && (() => {
          const m = STATUS_META[selected.status];
          return (
            <div>
              <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{selected.batchCode}</div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3 capitalize">{selected.checkType.replace("-", " ")}</div>
              <div className="mb-3">
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.tint, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>{m.label}</span>
              </div>
              <Field label="Test type"       inline><span className="text-sm capitalize">{selected.checkType.replace("-", " ")}</span></Field>
              <Field label="Batch"           inline><span className="text-sm font-mono">{selected.batchCode}</span></Field>
              <Field label="Operator"        inline><span className="text-sm">{selected.performedBy}</span></Field>
              <Field label="Equipment"       inline><span className="text-sm">Lab bench {selected.id.slice(-3).toUpperCase()}</span></Field>
              <Field label="Result"          inline><span className="text-sm" style={{ color: selected.status === "pass" ? "var(--ph-success)" : selected.status === "fail" ? "var(--ph-danger)" : "var(--ph-ink-default)" }}>{selected.result}</span></Field>
              {typeof selected.score === "number" && (
                <Field label="Score / Limits" inline><span className="text-sm font-semibold tabular-nums">{selected.score} / 100 <span className="text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>· spec ≥ 85</span></span></Field>
              )}
              <Field label="Pass / Fail"     inline>
                <span className="text-sm font-semibold uppercase" style={{ letterSpacing: "0.14em", color: selected.status === "pass" ? "var(--ph-success)" : selected.status === "fail" ? "var(--ph-danger)" : "var(--ph-warning)" }}>
                  {m.label}
                </span>
              </Field>
              {selected.severity && (
                <Field label="Severity" inline><SeverityBadge severity={selected.severity} /></Field>
              )}
              <Field label="Corrective action" inline>
                <span className="text-sm">{(selected as unknown as { correctiveAction?: string }).correctiveAction || (selected.status === "fail" ? "Rework & retest" : selected.status === "hold" ? "Hold until batch re-QC" : "None required")}</span>
              </Field>
              <Field label="Performed"       inline><span className="text-sm">{selected.performedRelative}</span></Field>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Attachments</div>
                <div className="text-xs p-2" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-sm)", color: "var(--ph-ink-muted)" }} data-testid="op-qc-attachments">{(selected as unknown as { attachments?: string }).attachments || `Certificate ${selected.batchCode}-COA.pdf · Chromatogram-${selected.id}.pdf`}</div>
              </div>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Certificates</div>
                <div className="text-xs p-2" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-sm)", color: "var(--ph-ink-muted)" }}>CoA · IFRA compliance · Stability report</div>
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Timeline</div>
                <AuditTrail collection="qc_checks" recordId={selected.id} testId="op-qc-audit" />
              </div>
              <div className="pt-4" data-testid="op-qc-docs">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>QC Report exports</div>
                <DocumentActions docKey="qc-report" recordId={selected.id} testId={`op-qc-report-${selected.id}`} compact />
              </div>
              <div className="pt-4 flex flex-wrap justify-end gap-2" data-testid="op-qc-actions">
                <Button variant="secondary" testId="op-qc-attach-cert" onClick={async () => {
                  try {
                    const label = `Certificate ${selected.batchCode}-COA.pdf`;
                    const prev = (selected as unknown as { attachments?: string }).attachments || "";
                    const next = prev ? `${prev} · ${label}` : label;
                    const row = await api.patch("qc_checks", selected.id, { attachments: next }, "Attached Certificate of Analysis");
                    upsertLocal("qc_checks", row as { id?: string });
                    setSelected(row as unknown as QcCheck);
                  } catch (e) { window.alert((e as Error).message); }
                }}>Attach Certificate</Button>
                <Button variant="secondary" testId="op-qc-attach-chroma" onClick={async () => {
                  try {
                    const label = `Chromatogram-${selected.id}.pdf`;
                    const prev = (selected as unknown as { attachments?: string }).attachments || "";
                    const next = prev ? `${prev} · ${label}` : label;
                    const row = await api.patch("qc_checks", selected.id, { attachments: next }, "Attached chromatogram");
                    upsertLocal("qc_checks", row as { id?: string });
                    setSelected(row as unknown as QcCheck);
                  } catch (e) { window.alert((e as Error).message); }
                }}>Attach Chromatogram</Button>
                <Button variant="secondary" testId="op-qc-corrective" onClick={async () => {
                  const action = window.prompt("Corrective action:", "Rework & retest") || "Rework & retest";
                  try {
                    const row = await api.patch("qc_checks", selected.id, { correctiveAction: action }, `Corrective action: ${action}`);
                    upsertLocal("qc_checks", row as { id?: string });
                    setSelected(row as unknown as QcCheck);
                  } catch (e) { window.alert((e as Error).message); }
                }}>Request Corrective Action</Button>
                <Button variant="secondary" testId="op-qc-retest" onClick={async () => {
                  try {
                    const row = await api.transition("qc_checks", selected.id, "hold", "Retest requested");
                    upsertLocal("qc_checks", row as { id?: string });
                    setSelected(row as unknown as QcCheck);
                  } catch (e) { window.alert((e as Error).message); }
                }}>Request Re-test</Button>
                <Button variant="secondary" testId="op-qc-hold" onClick={async () => {
                  try {
                    const row = await api.transition("qc_checks", selected.id, "hold", "Placed on hold");
                    upsertLocal("qc_checks", row as { id?: string });
                    setSelected(row as unknown as QcCheck);
                  } catch (e) { window.alert((e as Error).message); }
                }}>Hold</Button>
                <Button variant="secondary" testId="op-qc-reject" onClick={async () => {
                  try {
                    const row = await api.transition("qc_checks", selected.id, "fail", "Rejected");
                    upsertLocal("qc_checks", row as { id?: string });
                    setSelected(row as unknown as QcCheck);
                  } catch (e) { window.alert((e as Error).message); }
                }}>Reject / Fail</Button>
                <Button variant="secondary" testId="op-qc-send-approval" onClick={async () => {
                  try {
                    const row = await api.transition("qc_checks", selected.id, "hold", "Sent for approval");
                    upsertLocal("qc_checks", row as { id?: string });
                    setSelected(row as unknown as QcCheck);
                  } catch (e) { window.alert((e as Error).message); }
                }}>Send for Approval</Button>
                <Button testId="op-qc-approve" onClick={async () => {
                  try {
                    const row = await api.transition("qc_checks", selected.id, "pass", "Approved for release");
                    upsertLocal("qc_checks", row as { id?: string });
                    setSelected(row as unknown as QcCheck);
                  } catch (e) { window.alert((e as Error).message); }
                }}>Approve / Pass</Button>
              </div>
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
}
